import { generateUUID, LabelFacet, LabelAlign, LabelSpace, PrimitiveShape, RemoveFacetFromModelMessage, StartRoomTransitionMessage, UpdateFacetOnModelMessage, CreateLabelSpeechCommand, CreateLabelAction, MeshFacet, InteractionFacet } from "flavr-common";
import { LocalUserInfoMessage } from "@messages/session/PeerMessages";
import { Matrix4, Quaternion, Vector3 } from "flavr-math";
import { CreateCollisionShapeMessage, RemoveCollisionShapeMessage, UpdateCollisionShapeLocalTransformMessage } from "@messages/selection";
import { RequestSTTMessage, StartSTTRecordMessage, STTMessage } from "@messages/stt/STTMessages";
import { modelPool, useExternalMessages, computeLocalMatrixFromWorldMatrix } from "@modules/model";
import { ensureLocalPlayerControllersModel, hasLocalPlayerClientIdx } from "@modules/player";
import { InteractionHandler } from "../Interactions";
const tmpVector31 = new Vector3();
const tmpVector32 = new Vector3();
const tmpQuaternion1 = new Quaternion();
const tmpWorldMatrix = new Matrix4();
export class LabelModifierInteraction {
    constructor(uid, align) {
        this.uid = uid;
        this.align = align;
        this.behavior = LabelModifierInteraction.typestatic;
    }
    static is(params) {
        return params.behavior === LabelModifierInteraction.typestatic;
    }
}
LabelModifierInteraction.typestatic = "LabelModifierInteraction";
let microphoneHandleInfos;
const getMicrophoneHandleInfos = () => {
    if (microphoneHandleInfos !== undefined) {
        const handlesStillExist = microphoneHandleInfos.every((handle) => modelPool.hasModel(handle.uid));
        if (handlesStillExist) {
            return microphoneHandleInfos;
        }
    }
    const controllerRoot = ensureLocalPlayerControllersModel();
    microphoneHandleInfos = [
        {
            uid: modelPool.createModel("micTop", controllerRoot).getUID(),
            align: LabelAlign.Top,
            localPos: new Vector3(0, 0.5, 0),
            offset: new Vector3(0, 0.05, 0)
        },
        {
            uid: modelPool.createModel("micBottom", controllerRoot).getUID(),
            align: LabelAlign.Bottom,
            localPos: new Vector3(0, -0.5, 0),
            offset: new Vector3(0, -0.05, 0)
        },
        {
            uid: modelPool.createModel("micCenter1", controllerRoot).getUID(),
            align: LabelAlign.Center,
            localPos: new Vector3(0, 0, 0.5),
            offset: new Vector3(0, 0, 0.05)
        },
        {
            uid: modelPool.createModel("micCenter2", controllerRoot).getUID(),
            align: LabelAlign.Center,
            localPos: new Vector3(0, 0, -0.5),
            offset: new Vector3(0, 0, -0.05)
        },
        {
            uid: modelPool.createModel("micCenter3", controllerRoot).getUID(),
            align: LabelAlign.Center,
            localPos: new Vector3(0.5, 0, 0),
            offset: new Vector3(0.05, 0, 0)
        },
        {
            uid: modelPool.createModel("micCenter4", controllerRoot).getUID(),
            align: LabelAlign.Center,
            localPos: new Vector3(-0.5, 0, 0),
            offset: new Vector3(-0.05, 0, 0)
        }
    ];
    return microphoneHandleInfos;
};
export class CreateLabelSpeechInteractionHandler extends InteractionHandler {
    constructor(interactionModule) {
        super("CreateLabelSpeechInteractionHandler", [LabelModifierInteraction.typestatic], [CreateLabelAction.typestatic], [CreateLabelSpeechCommand.typestatic]);
        this.interactionModule = interactionModule;
        if (hasLocalPlayerClientIdx()) {
            getMicrophoneHandleInfos();
        }
        this.connection.subscribeByType(LocalUserInfoMessage, () => {
            getMicrophoneHandleInfos();
        });
        this.connection.subscribe([StartRoomTransitionMessage.typestatic], (msg) => {
            if (StartRoomTransitionMessage.is(msg)) {
                this.removeHandles();
            }
        });
    }
    performAction(params, context) {
        if (CreateLabelAction.is(params)) {
            if (this.activeUid !== undefined) {
                this.removeHandles();
            }
            if (context.targetUid === undefined)
                return;
            this.activeUid = context.targetUid;
            const messages = [];
            const transform = this.interactionModule.getModelTransformation(context.targetUid);
            if (transform) {
                for (const handle of getMicrophoneHandleInfos()) {
                    this.createHandle(messages, context.targetUid, handle, transform.pos, transform.ori, transform.scale);
                }
            }
            this.connection.sendMultiple(messages);
            this.interactionModule.subscribeWorldTransformEvent(this.activeUid, "MicrophoneHandles", (worldPosition, worldOrientation, worldScale) => {
                this.updateHandle(worldPosition, worldOrientation, worldScale);
            });
        }
    }
    onSelectButtonClick(params, _uid, _controller, _hitPoint) {
        if (LabelModifierInteraction.is(params) && this.activeUid === params.uid) {
            this.removeHandles();
        }
    }
    onSelectButtonHold(params, _uid, controller) {
        if (LabelModifierInteraction.is(params) && this.activeUid !== undefined) {
            this.interactionModule.lockSelection(controller.uid, true);
            this.connection.send(new StartSTTRecordMessage(controller.uid));
        }
    }
    onSelectButtonHoldReleased(params, _uid, controller) {
        if (!LabelModifierInteraction.is(params) || this.activeUid === undefined)
            return;
        this.interactionModule.lockSelection(controller.uid, false);
        this.connection.ask(new RequestSTTMessage(), (messages) => {
            this.removeHandles();
            if (messages.length !== 1 || params.uid === undefined)
                return;
            const msg = messages[0];
            if (!STTMessage.is(msg) || !msg.results.length)
                return;
            const size = msg.results[0].text.length / 12;
            const label = new LabelFacet(msg.results[0].text, {
                align: params.align,
                relativeSize: size,
                space: LabelSpace.Local
            });
            this.connection.send(new UpdateFacetOnModelMessage(params.uid, label));
        });
    }
    onSpeechCommand(params, cmd, cmdParams, controller) {
        var _a;
        if (CreateLabelSpeechCommand.is(params) && cmd === "label") {
            const text = cmdParams.text;
            if (text && text.value) {
                const messages = [];
                const props = useExternalMessages(messages);
                const parent = modelPool.getModelOrThrow(params.parentUid);
                const model = modelPool.createModel(`${params.prefixName}_${generateUUID()}`, parent, props);
                let align = LabelAlign.Top;
                const alignParam = cmdParams.align;
                if (alignParam) {
                    switch (alignParam.value) {
                        case "top":
                            align = LabelAlign.Top;
                            break;
                        case "bottom":
                            align = LabelAlign.Bottom;
                            break;
                        case "center":
                            align = LabelAlign.Center;
                            break;
                        case "right":
                            align = LabelAlign.Right;
                            break;
                        case "left":
                            align = LabelAlign.Left;
                            break;
                    }
                }
                const sizeParam = cmdParams.size;
                const size = (_a = sizeParam === null || sizeParam === void 0 ? void 0 : sizeParam.value) !== null && _a !== void 0 ? _a : 1;
                model.setTransformComponents(controller.position, controller.orientation, [1, 0.01, 1], props);
                model.setFacet(new LabelFacet(text.value, {
                    align,
                    space: LabelSpace.Local,
                    relativeSize: size
                }), props);
                this.connection.sendMultiple(messages);
            }
        }
    }
    createHandle(messages, uid, handle, modelPos, modelOrientation, modelScale) {
        const model = modelPool.getModel(handle.uid);
        if (model === undefined)
            return;
        const handleWorldPos = tmpVector31.copy(handle.localPos).multiply(modelScale).add(handle.offset).applyQuaternion(modelOrientation).add(modelPos);
        const handleWorldRot = tmpQuaternion1.copy(modelOrientation);
        tmpVector32.setScalar(0.1);
        const newModelWorldMatrix = tmpWorldMatrix.compose(handleWorldPos, handleWorldRot, tmpVector32);
        const props = useExternalMessages(messages);
        model.setFacet(new MeshFacet("meshes/Microphone", undefined, undefined, undefined, { needsRescale: false }), props);
        model.setFacet(new InteractionFacet({
            behaviors: [new LabelModifierInteraction(uid, handle.align)],
            description: "Labeltext aufnehmen"
        }), props);
        messages.push(new CreateCollisionShapeMessage(PrimitiveShape.Box, model.getUID(), true), new UpdateCollisionShapeLocalTransformMessage(model.getUID(), new Matrix4().toArray()));
        const localMatrix = computeLocalMatrixFromWorldMatrix(model, newModelWorldMatrix);
        model.setTransform(localMatrix, useExternalMessages(messages));
    }
    updateHandle(modelPosition, modelOrientation, modelScale) {
        if (this.activeUid === undefined)
            return;
        const messages = [];
        for (const handle of getMicrophoneHandleInfos()) {
            const handleWorldPos = tmpVector31.copy(handle.localPos).multiply(modelScale);
            if (handle.offset)
                handleWorldPos.add(handle.offset);
            handleWorldPos.applyQuaternion(modelOrientation).add(modelPosition);
            const handleWorldRot = tmpQuaternion1.copy(modelOrientation);
            tmpVector32.setScalar(0.1);
            tmpWorldMatrix.compose(handleWorldPos, handleWorldRot, tmpVector32);
            const model = modelPool.getModel(handle.uid);
            if (model === undefined)
                continue;
            const localMatrix = computeLocalMatrixFromWorldMatrix(model, tmpWorldMatrix);
            model.setTransform(localMatrix, useExternalMessages(messages));
        }
        this.connection.sendMultiple(messages);
    }
    removeHandles() {
        if (this.activeUid === undefined)
            return;
        const messages = [];
        for (const handle of getMicrophoneHandleInfos()) {
            messages.push(new RemoveFacetFromModelMessage(handle.uid, MeshFacet));
            messages.push(new RemoveFacetFromModelMessage(handle.uid, InteractionFacet));
            messages.push(new RemoveCollisionShapeMessage(handle.uid));
        }
        this.connection.sendMultiple(messages);
        this.interactionModule.unsubscribeWorldTransformEvent(this.activeUid, "MicrophoneHandles");
        this.activeUid = undefined;
    }
}
